import { ListTree, Plus, Trash2, X } from 'lucide-react';
import { useMemo } from 'react';
import {
  CONDITION_FIELDS,
  defaultCondition,
  defaultValue,
  emptyGroup,
  NO_VALUE_OPERATORS,
  OPERATORS,
  SOURCE_LABEL,
  UPDATE_FIELD_LABEL,
  useAssignees,
  useDepartments,
  indentedName,
  type FieldDef,
  type OptionSource,
} from '../lib/automation';
import { PERSON_TYPE_LABEL } from '../lib/format';
import { useBuildings, useCategories } from '../lib/hooks';
import { modelLabel, useCatalog } from '../lib/parts';
import { useSoftwareList } from '../lib/software';
import type { Assignee, Condition, ConditionGroup, ConditionValue, Department, TraceNode, WorkflowTrigger } from '../lib/types';
import { WORK_ORDER_CATEGORY_LABEL, WORK_ORDER_PRIORITY_LABEL, WORK_ORDER_STATUS_LABEL } from '../lib/work-orders';
import { cn, Input, Select } from './ui';

const MAX_DEPTH = 3;

export interface Option {
  value: string;
  label: string;
}

export interface ConditionOptions {
  departments: Department[];
  users: Assignee[];
  categories: Option[];
  models: Option[];
  buildings: Option[];
  software: Option[];
  /** Every software's own problem types, each labelled with the software it belongs to. */
  softwareProblemTypes: Option[];
}

const entries = (labels: Record<string, string>): Option[] => Object.entries(labels).map(([value, label]) => ({ value, label }));

/** Everything the condition builder offers in its dropdowns. */
export function useConditionOptions(): ConditionOptions {
  const departments = useDepartments();
  const users = useAssignees();
  const categories = useCategories();
  const catalog = useCatalog();
  const buildings = useBuildings();
  const software = useSoftwareList();
  return useMemo(
    () => ({
      departments: departments.data ?? [],
      users: users.data ?? [],
      categories: (categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
      models: (catalog.data ?? []).flatMap((m) => m.models.map((model) => ({ value: model.id, label: modelLabel({ name: model.name, manufacturer: m }) }))),
      buildings: (buildings.data ?? []).map((b) => ({ value: b.id, label: b.name })),
      software: (software.data ?? []).map((entry) => ({ value: entry.id, label: entry.name })),
      // Problem types repeat across software ("Can't sign in"), so each says which software it belongs to.
      softwareProblemTypes: (software.data ?? []).flatMap((entry) =>
        entry.problemTypes.map((type) => ({ value: type.id, label: `${entry.name} — ${type.name}` })),
      ),
    }),
    [departments.data, users.data, categories.data, catalog.data, buildings.data, software.data],
  );
}

function optionsFor(source: OptionSource, options: ConditionOptions): Option[] {
  switch (source) {
    case 'status':
      return entries(WORK_ORDER_STATUS_LABEL);
    case 'category':
      return entries(WORK_ORDER_CATEGORY_LABEL);
    case 'source':
      return entries(SOURCE_LABEL);
    case 'personType':
      return entries(PERSON_TYPE_LABEL);
    case 'commentBy':
      return [
        { value: 'REQUESTER', label: 'The requester' },
        { value: 'STAFF', label: 'The technology team' },
      ];
    case 'assetCategory':
      return options.categories;
    case 'assetModel':
      return options.models;
    case 'building':
      return options.buildings;
    case 'software':
      return options.software;
    case 'softwareProblemType':
      return options.softwareProblemTypes;
    case 'updateField':
      return entries(UPDATE_FIELD_LABEL);
  }
}

function valueOptions(field: FieldDef, options: ConditionOptions): Option[] {
  if (field.type === 'user') return options.users.map((u) => ({ value: u.id, label: u.name }));
  if (field.type === 'department') return options.departments.map((d) => ({ value: d.id, label: d.path }));
  if (field.type === 'priority') return entries(WORK_ORDER_PRIORITY_LABEL);
  return field.options ? optionsFor(field.options, options) : [];
}

/** A value in words: "Open, Waiting", "IT › Networking", “wifi”. */
export function describeValue(fieldKey: string | undefined, value: TraceNode['actual'] | ConditionValue | undefined, options: ConditionOptions) {
  const field = fieldKey ? CONDITION_FIELDS[fieldKey] : undefined;
  if (value === null || value === undefined || value === '') return field?.type === 'boolean' ? 'no' : 'nothing';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return String(value);
  const known = field ? valueOptions(field, options) : [];
  const label = (v: string) => known.find((o) => o.value === v)?.label ?? (field && ['user', 'department', 'reference'].includes(field.type) ? 'something removed' : v);
  if (Array.isArray(value)) return value.length ? value.map(label).join(', ') : 'nothing';
  return field?.type === 'text' ? `“${value}”` : label(value);
}

export function describeCondition(condition: { field?: string; operator?: string; value?: ConditionValue }, options: ConditionOptions) {
  const field = condition.field ? CONDITION_FIELDS[condition.field] : undefined;
  if (!field) return 'Unknown condition';
  const operator = OPERATORS[field.type].find((o) => o.value === condition.operator)?.label ?? condition.operator;
  return NO_VALUE_OPERATORS.has(condition.operator ?? '')
    ? `${field.label} ${operator}`
    : `${field.label} ${operator} ${describeValue(condition.field, condition.value, options)}`;
}

/** A dropdown that adds values as removable chips. */
export function ChipSelect({
  options,
  value,
  onChange,
  placeholder = 'Choose…',
  className,
}: {
  options: Option[];
  value: string[];
  onChange(value: string[]): void;
  placeholder?: string;
  className?: string;
}) {
  const remaining = options.filter((o) => !value.includes(o.value));
  return (
    <div className={cn('flex min-w-0 flex-1 flex-wrap items-center gap-1.5', className)}>
      {value.map((selected) => {
        const option = options.find((o) => o.value === selected);
        return (
          <span
            key={selected}
            className={cn(
              'inline-flex items-center gap-1 rounded-md py-0.5 pr-1 pl-2 text-xs font-medium ring-1 ring-inset',
              option ? 'bg-brand-50 text-brand-800 ring-brand-600/20' : 'bg-red-50 text-red-700 ring-red-600/20',
            )}
          >
            {option?.label ?? 'Removed'}
            <button type="button" onClick={() => onChange(value.filter((v) => v !== selected))} className="rounded p-0.5 hover:bg-white/70" aria-label="Remove">
              <X className="size-3" />
            </button>
          </span>
        );
      })}
      {remaining.length > 0 && (
        <Select
          value=""
          onChange={(e) => e.target.value && onChange([...value, e.target.value])}
          className="h-8 w-auto min-w-[9rem] text-xs"
          aria-label={placeholder}
        >
          <option value="">{value.length ? 'Add another…' : placeholder}</option>
          {remaining.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
}

function ValueInput({ condition, field, options, onChange }: { condition: Condition; field: FieldDef; options: ConditionOptions; onChange(value: ConditionValue): void }) {
  const choices = valueOptions(field, options);
  if (Array.isArray(condition.value) || ['in', 'not_in', 'includes', 'excludes'].includes(condition.operator)) {
    return <ChipSelect options={choices} value={Array.isArray(condition.value) ? condition.value : []} onChange={onChange} />;
  }
  if (field.type === 'department') {
    return (
      <Select value={String(condition.value ?? '')} onChange={(e) => onChange(e.target.value)} className="w-full sm:w-64">
        <option value="">Choose a department</option>
        {options.departments.map((department) => (
          <option key={department.id} value={department.id}>
            {indentedName(department)}
          </option>
        ))}
      </Select>
    );
  }
  if (field.type === 'priority') {
    return (
      <Select value={String(condition.value ?? 'HIGH')} onChange={(e) => onChange(e.target.value)} className="w-full sm:w-36">
        {choices.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  }
  if (field.type === 'number') {
    return (
      <Input
        type="number"
        min={0}
        value={condition.value === null || condition.value === undefined ? '' : String(condition.value)}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="w-24"
      />
    );
  }
  return (
    <Input
      value={typeof condition.value === 'string' ? condition.value : ''}
      onChange={(e) => onChange(e.target.value)}
      maxLength={2000}
      placeholder={condition.operator === 'contains_any' ? 'wifi, wireless, internet' : 'Text'}
      className="min-w-[10rem] flex-1"
    />
  );
}

function ConditionRow({
  condition,
  trigger,
  options,
  onChange,
  onRemove,
}: {
  condition: Condition;
  trigger: WorkflowTrigger;
  options: ConditionOptions;
  onChange(condition: Condition): void;
  onRemove(): void;
}) {
  const field = CONDITION_FIELDS[condition.field] ?? CONDITION_FIELDS.category;
  const available = Object.entries(CONDITION_FIELDS).filter(([key, def]) => !def.triggers || def.triggers.includes(trigger) || key === condition.field);
  const groups = [...new Set(available.map(([, def]) => def.group))];
  const unavailable = field.triggers && !field.triggers.includes(trigger);

  return (
    <div className={cn('flex flex-wrap items-center gap-2 rounded-md p-2', unavailable ? 'bg-red-50' : 'bg-slate-50')}>
      <Select value={condition.field} onChange={(e) => onChange(defaultCondition(e.target.value))} className="w-full sm:w-56" aria-label="Field">
        {groups.map((group) => (
          <optgroup key={group} label={group}>
            {available
              .filter(([, def]) => def.group === group)
              .map(([key, def]) => (
                <option key={key} value={key}>
                  {def.label}
                </option>
              ))}
          </optgroup>
        ))}
      </Select>
      <Select
        value={condition.operator}
        onChange={(e) => {
          const operator = e.target.value;
          const fresh = defaultValue(field.type, operator);
          const keep = Array.isArray(fresh) === Array.isArray(condition.value) && !NO_VALUE_OPERATORS.has(operator) && condition.value !== null;
          onChange({ ...condition, operator, value: keep ? condition.value : fresh });
        }}
        className="w-full sm:w-44"
        aria-label="Comparison"
      >
        {OPERATORS[field.type].map((operator) => (
          <option key={operator.value} value={operator.value}>
            {operator.label}
          </option>
        ))}
      </Select>
      {!NO_VALUE_OPERATORS.has(condition.operator) && (
        <ValueInput condition={condition} field={field} options={options} onChange={(value) => onChange({ ...condition, value })} />
      )}
      <button type="button" onClick={onRemove} className="ml-auto rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-red-600" aria-label="Remove condition">
        <Trash2 className="size-4" />
      </button>
      {unavailable && <p className="w-full text-xs text-red-700">This field only works with a different trigger. Change or remove it.</p>}
    </div>
  );
}

function GroupEditor({
  group,
  depth,
  trigger,
  options,
  onChange,
  onRemove,
}: {
  group: ConditionGroup;
  depth: number;
  trigger: WorkflowTrigger;
  options: ConditionOptions;
  onChange(group: ConditionGroup): void;
  onRemove?: () => void;
}) {
  const setChild = (index: number, child: Condition | ConditionGroup) =>
    onChange({ ...group, children: group.children.map((c, i) => (i === index ? child : c)) });
  const removeChild = (index: number) => onChange({ ...group, children: group.children.filter((_, i) => i !== index) });
  const joiner = group.match === 'all' ? 'and' : 'or';

  return (
    <div className={cn('space-y-2 rounded-lg border p-3', depth === 1 ? 'border-slate-200 bg-white' : 'border-brand-200 bg-brand-50/40')}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">{depth === 1 ? 'Run when' : 'Group:'}</span>
        <Select value={group.match} onChange={(e) => onChange({ ...group, match: e.target.value as 'all' | 'any' })} className="w-auto" aria-label="Match">
          <option value="all">all of these are true</option>
          <option value="any">any of these is true</option>
        </Select>
        {onRemove && (
          <button type="button" onClick={onRemove} className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-white hover:text-red-600">
            <Trash2 className="size-3.5" /> Remove group
          </button>
        )}
      </div>

      {group.children.length === 0 && (
        <p className="px-1 text-sm text-slate-500">{depth === 1 ? 'No conditions yet, so every ticket matches.' : 'This group is empty.'}</p>
      )}

      {group.children.map((child, index) => (
        <div key={index} className="flex items-start gap-2">
          <span className="mt-3 w-8 shrink-0 text-right text-[11px] font-semibold tracking-wide text-slate-400 uppercase">{index > 0 ? joiner : ''}</span>
          <div className="min-w-0 flex-1">
            {child.type === 'group' ? (
              <GroupEditor
                group={child}
                depth={depth + 1}
                trigger={trigger}
                options={options}
                onChange={(next) => setChild(index, next)}
                onRemove={() => removeChild(index)}
              />
            ) : (
              <ConditionRow
                condition={child}
                trigger={trigger}
                options={options}
                onChange={(next) => setChild(index, next)}
                onRemove={() => removeChild(index)}
              />
            )}
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-1 pl-10">
        <button
          type="button"
          onClick={() => onChange({ ...group, children: [...group.children, defaultCondition()] })}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
        >
          <Plus className="size-3.5" /> Condition
        </button>
        {depth < MAX_DEPTH && (
          <button
            type="button"
            onClick={() => onChange({ ...group, children: [...group.children, emptyGroup(group.match === 'all' ? 'any' : 'all')] })}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
          >
            <ListTree className="size-3.5" /> Group of conditions
          </button>
        )}
      </div>
    </div>
  );
}

/** Visual AND / OR condition builder with nested groups. */
export function ConditionBuilder({
  value,
  onChange,
  trigger,
  options,
}: {
  value: ConditionGroup;
  onChange(group: ConditionGroup): void;
  trigger: WorkflowTrigger;
  options: ConditionOptions;
}) {
  return <GroupEditor group={value} depth={1} trigger={trigger} options={options} onChange={onChange} />;
}
